import api from './api'
import {
  WorkOrder,
  CreateWorkOrderRequest,
  UpdateWorkOrderRequest,
  WorkOrderCompletionCheck,
} from '../types/workOrder'
import { getApiBaseUrl } from '@utils/apiBaseUrl'

export const workOrderService = {
  getAll: async (params?: {
    workshopCompanyId?: number
    customerCompanyId?: number
    vehicleId?: number
    status?: string
    page?: number
    size?: number
  }) => {
    const response = await api.get<{ success: boolean; data: WorkOrder[] }>('/work-orders/search', {
      params,
    })
    return response.data.data
  },

  getById: async (id: number, mechanicId?: number) => {
    const params: any = {}
    if (mechanicId) {
      params.mechanicId = mechanicId
    }
    const response = await api.get<{ success: boolean; data: WorkOrder }>(`/work-orders/${id}`, { params })
    return response.data.data
  },

  getByWorkshop: async (workshopCompanyId: number, page = 0, size = 20) => {
    const response = await api.get<{ success: boolean; data: WorkOrder[] }>(
      `/work-orders/workshop/${workshopCompanyId}`,
      { params: { page, size } }
    )
    return response.data.data
  },

  getByCustomer: async (customerCompanyId: number, workshopCompanyId?: number, page = 0, size = 20) => {
    const params: any = { page, size }
    if (workshopCompanyId) {
      params.workshopCompanyId = workshopCompanyId
    }
    const response = await api.get<{ success: boolean; data: WorkOrder[] }>(
      `/work-orders/customer/${customerCompanyId}`,
      { params }
    )
    return response.data.data
  },

  getByVehicle: async (vehicleId: number, page = 0, size = 100, workshopCompanyId?: number) => {
    const params: Record<string, number> = { page, size }

    // Prefer deterministic endpoint (workshop + vehicle) when we have workshopCompanyId.
    // This avoids production cases where /work-orders/vehicle/{id} returns empty/500 due to data inconsistencies.
    if (workshopCompanyId != null) {
      const response = await api.get<{ success: boolean; data: WorkOrder[] }>(
        `/work-orders/workshop/${workshopCompanyId}/vehicle/${vehicleId}`,
        { params }
      )
      return response.data.data
    }

    const response = await api.get<{ success: boolean; data: WorkOrder[] }>(
      `/work-orders/vehicle/${vehicleId}`,
      { params }
    )
    return response.data.data
  },

  create: async (data: CreateWorkOrderRequest) => {
    const response = await api.post<{ success: boolean; data: WorkOrder }>('/work-orders', data)
    return response.data.data
  },

  update: async (id: number, data: UpdateWorkOrderRequest) => {
    const response = await api.put<{ success: boolean; data: WorkOrder }>(
      `/work-orders/${id}`,
      data
    )
    return response.data.data
  },

  addServiceItem: async (
    workOrderId: number,
    data: {
      costId?: number
      serviceDescription: string
      hours: number
      hourlyRate: number
      notes?: string
    }
  ) => {
    const response = await api.post<{ success: boolean; data: import('../types/workOrder').WorkOrderServiceItem }>(
      `/work-orders/${workOrderId}/service-items`,
      data
    )
    return response.data.data
  },

  addPieceItem: async (
    workOrderId: number,
    data: {
      costId?: number
      pieceId?: number
      pieceDescription: string
      quantity: number
      unitPrice: number
      notes?: string
    }
  ) => {
    const response = await api.post<{ success: boolean; data: import('../types/workOrder').WorkOrderPieceItem }>(
      `/work-orders/${workOrderId}/piece-items`,
      data
    )
    return response.data.data
  },

  addServiceFromCatalog: async (workOrderId: number, costId: number, hours: number, notes?: string) => {
    const response = await api.post<{ success: boolean; data: import('../types/workOrder').WorkOrderServiceItem }>(
      `/work-orders/${workOrderId}/service-items/from-catalog`,
      { costId, hours, notes }
    )
    return response.data.data
  },

  addPieceFromCatalog: async (workOrderId: number, costId: number, quantity: number, notes?: string) => {
    const response = await api.post<{ success: boolean; data: import('../types/workOrder').WorkOrderPieceItem }>(
      `/work-orders/${workOrderId}/piece-items/from-catalog`,
      { costId, quantity, notes }
    )
    return response.data.data
  },

  removeServiceItem: async (workOrderId: number, itemId: number) => {
    await api.delete(`/work-orders/${workOrderId}/service-items/${itemId}`)
  },

  removePieceItem: async (workOrderId: number, itemId: number) => {
    await api.delete(`/work-orders/${workOrderId}/piece-items/${itemId}`)
  },

  addBudgetCost: async (
    workOrderId: number,
    data: { description: string; amount: number }
  ) => {
    const response = await api.post<{ success: boolean; data: import('../types/workOrder').WorkOrderBudgetCost }>(
      `/work-orders/${workOrderId}/budget-costs`,
      data
    )
    return response.data.data
  },

  removeBudgetCost: async (workOrderId: number, costId: number) => {
    await api.delete(`/work-orders/${workOrderId}/budget-costs/${costId}`)
  },

  approve: async (id: number, approvedBy: number) => {
    const response = await api.put<{ success: boolean; data: WorkOrder }>(
      `/work-orders/${id}/approve`,
      null,
      { params: { approvedBy } }
    )
    return response.data.data
  },

  cancel: async (id: number, reason?: string) => {
    const response = await api.put<{ success: boolean; data: WorkOrder }>(
      `/work-orders/${id}/cancel`,
      null,
      { params: { reason } }
    )
    return response.data.data
  },

  markAsInvoiced: async (id: number, invoiceNumber: string) => {
    const response = await api.put<{ success: boolean; data: WorkOrder }>(
      `/work-orders/${id}/invoice`,
      null,
      { params: { invoiceNumber } }
    )
    return response.data.data
  },

  getStats: async (workshopCompanyId: number) => {
    const response = await api.get<{ success: boolean; data: any }>(
      `/work-orders/stats/workshop/${workshopCompanyId}`
    )
    return response.data.data
  },

  getCompletionReadiness: async (id: number) => {
    const response = await api.get<{ success: boolean; data: WorkOrderCompletionCheck }>(
      `/work-orders/${id}/completion-readiness`
    )
    return response.data.data
  },

  updateStatus: async (id: number, status: string, changedBy?: number, notes?: string) => {
    const response = await api.put<{ success: boolean; data: WorkOrder }>(
      `/work-orders/${id}/status`,
      null,
      { params: { status, changedBy, notes } }
    )
    return response.data.data
  },

  confirmScheduledArrival: async (id: number, changedBy?: number) => {
    const response = await api.post<{ success: boolean; data: WorkOrder }>(
      `/work-orders/${id}/confirm-arrival`,
      null,
      { params: { changedBy } }
    )
    return response.data.data
  },

  cancelScheduling: async (id: number, changedBy?: number) => {
    const response = await api.post<{ success: boolean; data: WorkOrder }>(
      `/work-orders/${id}/cancel-scheduling`,
      null,
      { params: { changedBy } }
    )
    return response.data.data
  },

  getStatusHistory: async (id: number) => {
    const response = await api.get<{ success: boolean; data: any[] }>(
      `/work-orders/${id}/status-history`
    )
    return response.data.data
  },

  /** Obtém o PDF como blob URL para pré-visualização. O caller deve revogar a URL quando fechar. */
  fetchPdfBlobUrl: async (id: number, uploadedBy: number = 1): Promise<string> => {
    const token = localStorage.getItem('token')
    const baseURL = getApiBaseUrl()

    const response = await fetch(
      `${baseURL}/work-orders/${id}/pdf?uploadedBy=${uploadedBy}&disposition=inline&regenerate=true`,
      {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
      }
    )
    if (!response.ok) {
      const msg =
        response.status === 403
          ? 'Sem permissão para ver este PDF.'
          : response.status === 500
            ? 'Erro ao gerar ou abrir o PDF. Tente novamente.'
            : `Erro ao abrir o PDF (${response.status}).`
      throw new Error(msg)
    }
    const blob = await response.blob()
    const contentType = response.headers.get('content-type') || blob.type || ''
    const isPdf = contentType.includes('application/pdf') || blob.type.includes('application/pdf')
    if (!isPdf || blob.size === 0) {
      throw new Error('A resposta não é um PDF válido. Tente novamente.')
    }
    return window.URL.createObjectURL(blob)
  },

  /** Abre o PDF numa nova aba (usa fetchPdfBlobUrl). Pode ser bloqueado por popup blockers. */
  openPdfInNewTab: async (id: number, uploadedBy: number = 1) => {
    const url = await workOrderService.fetchPdfBlobUrl(id, uploadedBy)
    const w = window.open(url, '_blank', 'noopener,noreferrer')
    if (!w) {
      window.URL.revokeObjectURL(url)
      throw new Error('Não foi possível abrir o PDF. Verifique se o bloqueador de pop-ups está desativado.')
    }
    setTimeout(() => window.URL.revokeObjectURL(url), 60000)
  },

  downloadPdf: async (id: number, uploadedBy: number = 1) => {
    const token = localStorage.getItem('token')
    const baseURL = getApiBaseUrl()

    const response = await fetch(`${baseURL}/work-orders/${id}/pdf?uploadedBy=${uploadedBy}&regenerate=true`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!response.ok) {
      const msg =
        response.status === 403
          ? 'Sem permissão para descarregar este PDF.'
          : 'Erro ao fazer download do PDF'
      throw new Error(msg)
    }

    const blob = await response.blob()
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', `Folha-${id}.pdf`)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  },
}


